import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { WIDGET_REGISTRY } from './registry';
import { GRID_COLS } from './types';
import { usePermissions } from '../hooks/usePermissions';
import type { WidgetCategory, WidgetDefinition, WidgetLayoutItem } from './types';

interface WidgetPickerProps {
  open: boolean;
  onClose: () => void;
  onAdd: (widgetId: string) => void;
  currentWidgets: WidgetLayoutItem[];
}

const CATEGORY_LABELS: Record<WidgetCategory, string> = {
  personal: 'Persönlich',
  audit: 'Audits & Termine',
  admin: 'Administration',
  info: 'Informationen',
};

const CATEGORY_ORDER: WidgetCategory[] = ['personal', 'audit', 'admin', 'info'];

const WidgetPicker: React.FC<WidgetPickerProps> = ({
  open,
  onClose,
  onAdd,
  currentWidgets,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { hasPermission } = usePermissions();

  const availableWidgets = useMemo(() => {
    return WIDGET_REGISTRY.filter((w) => {
      if (w.requiredPermission && !hasPermission(w.requiredPermission)) return false;
      return true;
    });
  }, [hasPermission]);

  const groupedWidgets = useMemo(() => {
    const groups: Record<WidgetCategory, WidgetDefinition[]> = {
      personal: [],
      audit: [],
      admin: [],
      info: [],
    };
    availableWidgets.forEach((w) => {
      groups[w.category].push(w);
    });
    return groups;
  }, [availableWidgets]);

  const isAdded = (widgetId: string) =>
    currentWidgets.some((w) => w.widget_id === widgetId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: isDark ? '#1a2035' : '#ffffff',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          Widget hinzufügen
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {CATEGORY_ORDER.map((category) => {
          const widgets = groupedWidgets[category];
          if (widgets.length === 0) return null;

          return (
            <Box key={category} sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: isDark ? '#8899aa' : '#5f7082',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1.5,
                }}
              >
                {CATEGORY_LABELS[category]}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {widgets.map((w) => {
                  const IconComp = w.icon;
                  const added = isAdded(w.id);

                  return (
                    <Box
                      key={w.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        borderRadius: 2,
                        border: `1px solid ${isDark ? '#2e3560' : '#dfe3e8'}`,
                        bgcolor: isDark ? '#212744' : '#fafbfc',
                        transition: 'border-color 0.2s',
                        '&:hover': {
                          borderColor: alpha(theme.palette.primary.main, 0.4),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          flexShrink: 0,
                        }}
                      >
                        <IconComp sx={{ fontSize: 22, color: theme.palette.primary.main }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {w.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#8899aa' : '#5f7082' }}>
                          {w.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                          <Chip
                            label={`${w.defaultW}/${GRID_COLS} Spalten`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              color: isDark ? '#8899aa' : '#5f7082',
                            }}
                          />
                          <Chip
                            label="frei skalierbar"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.6rem',
                              bgcolor: alpha(theme.palette.success.main, 0.08),
                              color: isDark ? '#8899aa' : '#5f7082',
                            }}
                          />
                        </Box>
                      </Box>
                      <Button
                        variant={added ? 'outlined' : 'contained'}
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                        onClick={() => onAdd(w.id)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          minWidth: 100,
                          height: 32,
                          flexShrink: 0,
                        }}
                      >
                        {added ? 'Nochmal' : 'Hinzufügen'}
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

export default WidgetPicker;
